import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { CloseDialogIcon } from "assets/styles/icons";
import { Grid, Stack } from "@mui/system";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Formik } from "formik";
import { EarlyAccessSchema } from "shared/helpers/Schema";
import {
  AppFormikTextArea,
  AppFormikTextField,
  AppFormikTextFieldNumber,
  AppPrimaryButton,
  StyledDialog,
} from "shared/components/AppComponents";
import axios from "axios";
import MobileAppBar from "./MenuMobile";

interface LandingContextType {
  earlyAccess: boolean;
  setEarlyAccess: React.Dispatch<React.SetStateAction<any>>;
}

const LandingContext = createContext<LandingContextType | null>(null);

export const useLanding = () => {
  const context = useContext(LandingContext);
  if (!context) {
    throw new Error("useLanding must be used within LandingLayout");
  }
  return context;
};

const LandingLayout = ({ children }: { children: any }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const sameHost =
    window.location.hostname === process.env.REACT_APP_LANDING_ROUTE;

  const [earlyAccess, setEarlyAccess] = useState(false);
  const [successEarlyAcces, setSuccessEarlyAcces] = useState(false);

  const AnimatedButton = () => {
    const [onSecureHover, setSecureHover] = useState(false);

    return (
      <motion.div
        initial={{ paddingRight: "0px" }}
        animate={{ paddingRight: "16px" }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
        }}
      >
        <Button
          variant="contained"
          sx={{
            height: "32px",
            borderRadius: "8px",
            backgroundColor: "white",
            color: "#0053fd",
            fontSize: "15px",
            fontWeight: "normal",
            boxShadow: "none",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#f0f0f0",
              boxShadow: "none",
            },
            minWidth: "8.75rem",
          }}
          onMouseEnter={() =>
            setTimeout(() => {
              setSecureHover(true);
            }, 100)
          }
          onClick={() => setEarlyAccess(true)}
        >
          Secure my spot
        </Button>
      </motion.div>
    );
  };

  const API_KEY = "568db595c6b8de3a2ec2ad348c172c0c";
  const FORM_ID = "252787312425863";

  const url = `https://api.jotform.com/form/${FORM_ID}/submissions?apiKey=${API_KEY}`;

  const handleSubmitForm = (formValues) => {
    console.log(formValues);

    let formattedPhone = formValues.phoneNumber;

    formattedPhone = formattedPhone.replace(/^\+?61\s*/, "");

    formattedPhone = formattedPhone.replace(/[^\d]/g, "");

    formattedPhone = `+61 ${formattedPhone}`;

    const dataForm = {
      submission: {
        "3": {
          first: formValues.firstName,
          last: formValues.lastName,
        },
        "4": formValues.email,
        "5": formattedPhone,
        "7": formValues.companyName,
        "8": formValues.jobTitle,
      },
    };

    axios
      .put(url, dataForm)
      .then((response) => {
        if (response.data.responseCode === 200) {
          setEarlyAccess(false);
          setSuccessEarlyAcces(true);
        }
      })
      .catch((error) => {
        console.error(
          "Error submitting form:",
          error.response ? error.response.data : error.message,
        );
      });
  };

  useEffect(() => {
    if (successEarlyAcces) {
      setTimeout(() => {
        setSuccessEarlyAcces(false);
        setEarlyAccess(false);
      }, 3000);
    }
  }, [successEarlyAcces]);

  const ApplyNowWhiteButton = () => {
    const showEarlyAccess = () => {
      setEarlyAccess(true);
    };

    return (
      <Button
        variant="contained"
        fullWidth
        sx={{
          height: "32px",
          borderRadius: "8px",
          backgroundColor: "white",
          border: "1px solid #E5E5E5",
          color: "#000000",
          fontSize: "15px",
          padding: "0 16px",
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "#f0f0f0",
            boxShadow: "none",
          },
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setEarlyAccess(true);
        }}
      >
        Apply now
        <Box
          component="img"
          src="/images/icon-arrow-right.svg"
          sx={{ marginLeft: "1rem" }}
        />
      </Button>
    );
  };

  return (
    <>
      {/* --------- Header */}
      {/* --------- Ads */}
      <StyledDialog open={earlyAccess} onClose={() => setEarlyAccess(false)}>
        <DialogContent sx={{ overflow: "hidden", padding: 0 }}>
          <Box sx={{ padding: "24px" }}>
            <Box>
              <Box mb={"0.5rem"}>
                <Box className="justify-space-between">
                  <Typography
                    variant="h4"
                    sx={{ fontSize: "28px", fontWeight: "600" }}
                  >
                    Register for early access
                  </Typography>
                  <IconButton onClick={() => setEarlyAccess(false)}>
                    <CloseDialogIcon />
                  </IconButton>
                </Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    color: "#474747",
                  }}
                >
                  Be among the first to explore EZIFIN’s smarter credit
                  solutions.
                </Typography>
                <Box mt={"1rem"}>
                  <Stack direction="row" spacing={2}></Stack>
                  {/* Formik */}
                  <Formik
                    enableReinitialize
                    validationSchema={EarlyAccessSchema}
                    validateOnMount
                    onSubmit={(formValues, formik) => {
                      handleSubmitForm(formValues);
                    }}
                    initialValues={{
                      firstName: "",
                      lastName: "",
                      email: "",
                      phoneNumber: "",
                      companyName: "",
                      jobTitle: "",
                    }}
                  >
                    {({ handleSubmit, errors, values, setSubmitting }) => {
                      console.log(errors);
                      return (
                        <form autoComplete="off">
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <AppFormikTextField
                                variant="outlined"
                                label="First name*"
                                name="firstName"
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <AppFormikTextField
                                variant="outlined"
                                label="Last name*"
                                name="lastName"
                              />
                            </Grid>
                          </Grid>

                          <AppFormikTextField
                            variant="outlined"
                            label="Email address*"
                            name="email"
                          />
                          <AppFormikTextFieldNumber
                            variant="outlined"
                            label=""
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                padding: 0,
                              },
                            }}
                            name="phoneNumber"
                            className="textfield-number"
                            notched={false}
                            outerLabel
                            placeholder="Phone number*"
                            inputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    defaultValue="61"
                                    label="Age"
                                    onChange={(event: any) => {
                                      console.log(event.target.value as string);
                                    }}
                                    sx={{
                                      "& .MuiSelect-select": {
                                        width: "3.5rem",
                                        outline: "none",
                                      },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          border: "1px solid #ededed",
                                        },
                                    }}
                                  >
                                    <MenuItem value="61">
                                      <Box sx={{ display: "flex" }}>
                                        <img
                                          src="/images/au-rectangle.png"
                                          style={{
                                            position: "relative",
                                            left: "-0.2rem",
                                            height: "16px",
                                            width: "20px",
                                            top: "0.2rem",
                                            borderRadius: "4px",
                                          }}
                                        />
                                        <Typography>+61</Typography>
                                      </Box>
                                    </MenuItem>
                                  </Select>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <AppFormikTextField
                                variant="outlined"
                                label="Company name*"
                                name="companyName"
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <AppFormikTextField
                                variant="outlined"
                                label="Job title*"
                                name="jobTitle"
                              />
                            </Grid>
                          </Grid>
                          <Box mt={"1rem"}>
                            <AppPrimaryButton
                              title="Submit"
                              fullWidth
                              className="submit-form"
                              onClick={handleSubmit}
                            />
                          </Box>
                        </form>
                      );
                    }}
                  </Formik>
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "15px", lineHeight: "20px" }}>
                  By submitting, you agree with our&nbsp;
                  <a
                    style={{
                      color: "black",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    className="footer-links"
                    href="/terms-of-service"
                    target="_blank"
                    // onClick={() => navigate("/terms-of-service")}
                  >
                    Terms of Service
                  </a>
                  &nbsp;&&nbsp;
                  <a
                    style={{
                      color: "black",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    className="footer-links"
                    href="/privacy-policy"
                    target="_blank"
                    // onClick={() => navigate("/privacy-policy")}
                  >
                    Privacy Policy
                  </a>
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </StyledDialog>

      <Dialog open={successEarlyAcces} onClose={() => setEarlyAccess(false)}>
        <Box sx={{ padding: "24px", textAlign: "center" }}>
          <img src="/images/success.gif" width={120} />
          <Box sx={{ padding: "12px" }}>
            <Typography
              sx={{
                fontSize: "28px",
                lineHeight: "34px",
                fontWeight: 800,
                marginTop: "0.5rem",
              }}
            >
              We’ve got your details
            </Typography>
            <Typography
              sx={{
                fontSize: "17px",
                lineHeight: "22px",
                fontWeight: 400,
                marginTop: "0.5rem",
              }}
            >
              {" "}
              Please allow us 24-48 hrs to address your query.
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* {location.pathname === "/" && (
        <Box
          sx={{
            backgroundColor: "#0053fd",
            padding: "8px 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
            <Box
              sx={{
                display: "flex",
                paddingRight: "2rem",
                alignItems: "center",
              }}
            >
              <img
                src="/images/ezifinconnect.gif"
                width="24"
                height="24"
                alt="gif logo"
              />
              <img
                src="/images/connect.svg"
                style={{ maxWidth: "99px" }}
                alt="connect logo"
              />
            </Box>

            <Box
              sx={{
                borderRight: "1px solid #ffffff3d",
                height: "1rem",
              }}
            ></Box>

            <Box
              component="span"
              sx={{
                marginLeft: "2rem",
                color: "white",
                fontWeight: 100,
                fontSize: "17px",
                [theme.breakpoints.down("md")]: {
                  display: "none",
                },
              }}
            >
              Shaping the future of alternate lending
            </Box>
          </Box>

          <Box sx={{ margin: "auto", width: "100%", textAlign: "right" }}>
            <AnimatedButton />
          </Box>
        </Box>
      )} */}

      {/* -------- Header Content */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #E5E5E5",
        }}
      >
        {/* Left Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "0 32px",
          }}
        >
          <Box
            component="img"
            src="/images/ezifin-logo.svg"
            alt="EZIFIN Logo"
            sx={{
              marginRight: "2rem",
              cursor: "pointer",
              position: "relative",
            }}
            onClick={() => {
              if (sameHost) {
                navigate("/");
                handleClose();
                return;
              }
              window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/`;
            }}
          />

          <Box>
            <IconButton
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                [theme.breakpoints.down("md")]: {
                  display: "none",
                },
                padding: "11px 0 11px 0",
                borderBottom: open
                  ? "2px solid #0053FD"
                  : "2px solid transparent",
                borderRadius: 0,
              }}
              onClick={handleClick}
            >
              <Typography sx={{ fontSize: "17px" }} style={{ color: "black" }}>
                Our Products
              </Typography>
              <Box
                component="img"
                src="/images/arrow-down.svg"
                alt="Dropdown Arrow"
                sx={{ width: "8px", position: "relative", top: "0.039vw" }}
              />
            </IconButton>
          </Box>
        </Box>

        {/* Right Section */}
        <Box
          sx={{
            display: "flex",
            gap: "2rem",
            alignItems: "center",
            [theme.breakpoints.down("md")]: {
              display: "none",
            },
          }}
        >
          <Box
            sx={{
              padding: "12px 0 12px 0",
              borderBottom:
                location.pathname === "/contact-us" ? "2px solid #0053FD" : "",
            }}
            onClick={() => {
              if (sameHost) {
                navigate("/contact-us");
                return;
              }
              window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/contact-us`;
            }}
          >
            <Typography
              sx={{
                fontSize: "17px",
                cursor: "pointer",
                color: location.pathname === "/contact-us" ? "#0053FD" : "",
              }}
            >
              Contact Us
            </Typography>
          </Box>
          <Box
            sx={{
              padding: "12px 0 12px 0",
              borderBottom:
                location.pathname === "/about-us" ? "2px solid #0053FD" : "",
              marginRight: "32px",
            }}
            onClick={() => {
              if (sameHost) {
                navigate("/about-us");
                return;
              }
              window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/about-us`;
            }}
          >
            <Typography
              sx={{
                fontSize: "17px",
                cursor: "pointer",
                color: location.pathname === "/about-us" ? "#0053FD" : "",
              }}
            >
              About Us
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "none",
            [theme.breakpoints.down("md")]: {
              display: "flex",
            },
            right: "1rem",
            position: "relative",
          }}
        >
          <MobileAppBar section={"product"} />
        </Box>
      </Box>

      {/* --------- Main Content */}
      <LandingContext.Provider value={{ earlyAccess, setEarlyAccess }}>
        <Box>{children}</Box>
      </LandingContext.Provider>

      {/* --------- Footer */}
      <Box
        sx={{
          padding: "0 7.813vw",
          background: "#0053FD",
        }}
      >
        <Box sx={{ display: "flex" }}>
          {/* Footer text content */}
          <Box
            sx={{
              width: "65%",
              padding: "72px 0 0 0",
              border: "1px solid #FFFFFF4D",
              [theme.breakpoints.down("md")]: {
                width: "85%",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                color: "white",
                gap: "4rem",
                padding: "0 2rem",
              }}
            >
              <Box sx={{ display: "flex" }}>
                <Box>
                  <Typography fontSize={15} fontWeight={600} mt={"1rem"}>
                    Our Products
                  </Typography>
                  <Typography
                    fontSize={15}
                    fontWeight={200}
                    mt={"1rem"}
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      if (sameHost) {
                        navigate("/?sbl");
                        return;
                      }
                      window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/?sbl`;
                    }}
                  >
                    Secured Business Loan
                  </Typography>
                  <Typography
                    fontSize={15}
                    fontWeight={200}
                    mt={"1rem"}
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      if (sameHost) {
                        navigate("/?rnd");
                        return;
                      }
                      window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/?rnd`;
                    }}
                  >
                    R&D Tax Incentive Financing
                  </Typography>
                </Box>
                <Box></Box>
              </Box>
              <Box>
                <Box sx={{ display: "flex" }}>
                  <Box>
                    <Typography fontSize={15} fontWeight={600} mt={"1rem"}>
                      Company
                    </Typography>
                    <Typography
                      fontSize={15}
                      fontWeight={200}
                      mt={"1rem"}
                      onClick={() => {
                        if (sameHost) {
                          navigate("/about-us");
                          return;
                        }
                        window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/about-us`;
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      About Us
                    </Typography>
                  </Box>
                  <Box></Box>
                </Box>
              </Box>
            </Box>

            {/* Privacy Term */}
            <Box
              sx={{
                display: "flex",
                marginTop: "5rem",
                border: "1px solid #FFFFFF4D",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "0 2rem",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    height: "72px",
                    alignItems: "center",
                  }}
                >
                  <a
                    style={{
                      color: "white",
                      fontWeight: 200,
                      cursor: "pointer",
                    }}
                    className="footer-links"
                    href="/privacy-policy"
                    target="_blank"
                    // onClick={() => navigate("/privacy-policy")}
                  >
                    Privacy Policy
                  </a>
                  <a
                    style={{
                      color: "white",
                      fontWeight: 200,
                      cursor: "pointer",
                    }}
                    className="footer-links pl-6"
                    href="/terms-of-service"
                    target="_blank"
                    // onClick={() => navigate("/terms-of-service")}
                  >
                    Terms of service
                  </a>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "white",
                      [theme.breakpoints.down("md")]: {
                        display: "none",
                      },
                    }}
                    className="sofia-pro-regular pl-6"
                  >
                    Ezi Financial Services Pty Ltd (EZIFIN)
                    <br />
                    ABN 95 628 254 455
                    <br />
                    Australian Credit Licence 515951
                  </Typography>
                </Box>
                <Box
                // sx={{
                //   display: "flex",
                //   // gap: "2rem",
                //   alignItems: "center",
                // }}
                ></Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {/* <img src="/images/instagram.svg" style={{ height: "20px" }} />
                  <img src="/images/facebook.svg" style={{ height: "20px" }} /> */}
                  <img
                    onClick={() =>
                      window.open(
                        "https://www.linkedin.com/company/ezifin/",
                        "_blank",
                      )
                    }
                    src="/images/linkedin.svg"
                    style={{ height: "20px", cursor: "pointer" }}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                margin: "1.5rem 0",
                color: "white",
                display: "none",
                [theme.breakpoints.down("md")]: {
                  display: "block",
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                }}
                className="sofia-pro-regular pl-6"
              >
                Ezi Financial Services Pty Ltd (EZIFIN)
                <br />
                ABN 95 628 254 455
                <br />
                Australian Credit Licence 515951
              </Typography>
            </Box>
            <Box
              sx={{
                height: "11.6vw",
                display: "grid",
                overflow: "hidden",
              }}
            >
              <img
                src="/images/footer-text.gif"
                style={{
                  position: "relative",
                  width: "32.292vw",
                  top: "-11vw",
                  left: "3.7vw",
                  transform: "scale(1.4)",
                }}
              />
            </Box>
          </Box>

          {/* Footer text image */}
          <Box
            sx={{
              [theme.breakpoints.down("md")]: {
                width: "0%",
                position: "absolute",
                right: "40vw",
                marginTop: "5vw",
              },
            }}
          >
            <img
              src="/images/footer-image.png"
              style={{
                width: "388px",
                position: "relative",
                marginTop: "5rem",
                marginLeft: "4rem",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Menus */}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": "basic-button",
          },
          root: {
            sx: {
              ".MuiList-root": {
                padding: 0,
              },
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "72vw",
          },
        }}
        sx={{
          "& .MuiMenu-paper": {
            backgroundColor: "#F8F8F6",
          },
          transform: "translate(0, 2rem)",
        }}
      >
        <Box sx={{ minHeight: "472px", background: "white" }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{ padding: "24px 0 16px 24px" }}
                className="cursor-pointer"
                onClick={() => {
                  if (sameHost) {
                    navigate("/?sbl");
                    handleClose();
                    return;
                  }
                  window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/?sbl`;
                }}
              >
                <Box
                  sx={{
                    padding: "24px",
                    border: "1px solid #E5E5E5",
                    borderRadius: "16px",
                    minHeight: "190px",
                  }}
                >
                  <img
                    src="/images/working-team1.jpg"
                    height={48}
                    style={{ borderRadius: "8px" }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "20px",
                      color: "#0A0A0A",
                      marginTop: "1rem",
                    }}
                    fontWeight={"600"}
                  >
                    Secured Business Loan
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "13px",
                      color: "#474747",
                      marginTop: "1rem",
                    }}
                  >
                    Flexible, Property-Secured Loans from $300k to $10M+
                  </Typography>

                  <Box className="mt-3">
                    <ApplyNowWhiteButton />
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{ padding: "24px 24px 16px 0" }}
                className="cursor-pointer"
                onClick={() => {
                  if (sameHost) {
                    navigate("/?rnd");
                    handleClose();
                    return;
                  }
                  window.location.href = `https://${process.env.REACT_APP_LANDING_ROUTE}/?rnd`;
                }}
              >
                <Box
                  sx={{
                    padding: "24px",
                    border: "1px solid #E5E5E5",
                    borderRadius: "16px",
                    minHeight: "190px",
                  }}
                >
                  <img
                    src="/images/working-team2.png"
                    height={48}
                    style={{ borderRadius: "8px" }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "20px",
                      color: "#0A0A0A",
                      marginTop: "1rem",
                    }}
                    fontWeight={"600"}
                  >
                    R&D Tax Incentive Financing
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "13px",
                      color: "#474747",
                      marginTop: "1rem",
                    }}
                  >
                    Accelerate Access to Your R&D Tax Refund – Fast and Flexible
                  </Typography>

                  <Box className="mt-3">
                    <ApplyNowWhiteButton />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Second Grid */}
          <Box sx={{ padding: "16px 24px", background: "#F8F8F6" }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    padding: "24px",
                    border: "1px solid #E5E5E5",
                    borderRadius: "16px",
                    background: "white",
                    minHeight: "10rem",
                  }}
                >
                  <Box
                    sx={{
                      color: "#0053FD",
                      background: "#F2F6FF",
                      borderRadius: "50%",
                      height: "30px",
                      width: "30px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      fontSize={"17px"}
                      fontWeight={600}
                      sx={{ position: "relative", top: "0.2rem" }}
                    >
                      #1
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "20px",
                      color: "#0A0A0A",
                      marginTop: "1rem",
                    }}
                    fontWeight={"600"}
                    minHeight={"2.55rem"}
                  >
                    Secured Business Loan
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "13px",
                      color: "#474747",
                      marginTop: "1rem",
                    }}
                  >
                    Businesses provide basic information about the funding
                    needs.
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    padding: "24px",
                    border: "1px solid #E5E5E5",
                    borderRadius: "16px",
                    background: "white",
                    minHeight: "10rem",
                  }}
                >
                  <Box
                    sx={{
                      color: "#0053FD",
                      background: "#F2F6FF",
                      borderRadius: "50%",
                      height: "30px",
                      width: "30px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      fontSize={"17px"}
                      fontWeight={600}
                      sx={{ position: "relative", top: "0.2rem" }}
                    >
                      #2
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "20px",
                      color: "#0A0A0A",
                      marginTop: "1rem",
                    }}
                    fontWeight={"600"}
                    minHeight={"2.55rem"}
                  >
                    Assessment
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "13px",
                      color: "#474747",
                      marginTop: "1rem",
                    }}
                  >
                    EZIFIN reviews eligibility, security, and designs a tailored
                    solution.
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    padding: "24px",
                    border: "1px solid #E5E5E5",
                    borderRadius: "16px",
                    background: "white",
                    minHeight: "10rem",
                  }}
                >
                  <Box
                    sx={{
                      color: "#0053FD",
                      background: "#F2F6FF",
                      borderRadius: "50%",
                      height: "30px",
                      width: "30px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      fontSize={"17px"}
                      fontWeight={600}
                      sx={{ position: "relative", top: "0.2rem" }}
                    >
                      #3
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "20px",
                      color: "#0A0A0A",
                      marginTop: "1rem",
                    }}
                    fontWeight={"600"}
                    minHeight={"2.55rem"}
                  >
                    Approval
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "13px",
                      color: "#474747",
                      marginTop: "1rem",
                    }}
                  >
                    Clear documentation and approvals provided for a faster
                    process.
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    padding: "24px",
                    border: "1px solid #E5E5E5",
                    borderRadius: "16px",
                    background: "white",
                    minHeight: "10rem",
                  }}
                >
                  <Box
                    sx={{
                      color: "#0053FD",
                      background: "#F2F6FF",
                      borderRadius: "50%",
                      height: "30px",
                      width: "30px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      fontSize={"17px"}
                      fontWeight={600}
                      sx={{ position: "relative", top: "0.2rem" }}
                    >
                      #4
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "20px",
                      color: "#0A0A0A",
                      marginTop: "1rem",
                    }}
                    fontWeight={"600"}
                    minHeight={"2.55rem"}
                  >
                    Funding
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "13px",
                      color: "#474747",
                      marginTop: "1rem",
                    }}
                  >
                    Funds are released with ongoing monitoring, support, and
                    flexibility.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default LandingLayout;
